import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { useSubscription } from '@/src/context/SubscriptionContext';
import {
  addParticipantUseCase,
  addRegisteredParticipantUseCase,
  addTournamentAdminUseCase,
  checkTournamentPermissionUseCase,
  deleteParticipantUseCase,
  generateBracketUseCase,
  getParticipantsByTournamentUseCase,
  getTournamentAdminsUseCase,
  getTournamentChampionUseCase,
  removeTournamentAdminUseCase,
  searchUserByEmailUseCase,
  tournamentRepository,
  sendNotificationUseCase,
  sendTournamentInvitationUseCase,
  tournamentInvitationDataSource,
} from '@/src/core/dependencies';
import { isValidParticipantCount, logger } from '@/src/core/utils';
import { AdminRole, ModerationError, Participant, Tournament, TournamentAdmin, User } from '@/src/domain/models';
import { SearchUsersUseCase } from '@/src/domain/usecases/SearchUsersUseCase';
import type { PermissionCheck } from '@/src/domain/usecases/CheckTournamentPermissionUseCase';
import { useTranslation } from '@/src/i18n';
import {
  TournamentDetailScreen as TournamentDetailScreenView,
  TournamentDetailScreenError,
  TournamentDetailScreenLoading,
  type TabKey,
} from '@/src/presentation/screens/TournamentDetailScreen';
import * as Clipboard from 'expo-clipboard';
import * as Linking from 'expo-linking';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, Share } from 'react-native';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { useInterstitialAd } from '@/src/presentation/hooks';

const searchUsersUseCase = new SearchUsersUseCase();

export default function TournamentDetailContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [champion, setChampion] = useState<Participant | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [participantName, setParticipantName] = useState('');
  const { user } = useAuth();
  const { checkAccess } = useSubscription();
  const [admins, setAdmins] = useState<TournamentAdmin[]>([]);
  const [searchEmail, setSearchEmail] = useState('');
  const { showAd } = useInterstitialAd();
  const [searchingUser, setSearchingUser] = useState(false);
  const [addAdminModalVisible, setAddAdminModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState<AdminRole>('admin');
  const [permissions, setPermissions] = useState<PermissionCheck>({ hasPermission: false });
  const [addParticipantModalVisible, setAddParticipantModalVisible] = useState(false);
  const [participantSearchQuery, setParticipantSearchQuery] = useState('');
  const [participantSearchResults, setParticipantSearchResults] = useState<User[]>([]);
  const [selectedParticipantUser, setSelectedParticipantUser] = useState<User | null>(null);
  const [searchingParticipant, setSearchingParticipant] = useState(false);
  const participantSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('participants');
  const [editingRules, setEditingRules] = useState(false);
  const [rulesText, setRulesText] = useState('');
  const canManageParticipants = permissions.userRole === 'owner' || permissions.userRole === 'admin';
  const canEditTournament = permissions.userRole === 'owner';
  const canManageAdmins = permissions.userRole === 'owner';
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | undefined>(undefined);

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (participantSearchTimer.current) clearTimeout(participantSearchTimer.current);
    if (participantSearchQuery.trim().length < 2) {
      setParticipantSearchResults([]);
      return;
    }
    participantSearchTimer.current = setTimeout(async () => {
      try {
        setSearchingParticipant(true);
        const results = await searchUsersUseCase.execute(participantSearchQuery.trim());
        setParticipantSearchResults(results);
      } catch {
        setParticipantSearchResults([]);
      } finally {
        setSearchingParticipant(false);
      }
    }, 400);
    return () => {
      if (participantSearchTimer.current) clearTimeout(participantSearchTimer.current);
    };
  }, [participantSearchQuery]);

  const loadData = async () => {
    if (!id || !user) return;

    try {
      setLoading(true);
      const [tournamentData, participantsData, adminsData, permissionsData] = await Promise.all([
        tournamentRepository.getById(id),
        getParticipantsByTournamentUseCase.execute(id),
        getTournamentAdminsUseCase.execute(id),
        checkTournamentPermissionUseCase.execute({
          tournamentId: id,
          userId: user.id,
          permission: 'view'
        })
      ]);

      setTournament(tournamentData);
      setRulesText(tournamentData?.rules || '');
      setLocationName(tournamentData?.location || '');
      if (tournamentData?.locationLat && tournamentData?.locationLng) {
        setSelectedLocation({
          latitude: tournamentData.locationLat,
          longitude: tournamentData.locationLng,
        });
      }
      setSelectedDate(tournamentData?.startDate || new Date());
      setInviteCode(tournamentData?.inviteCode);
      setParticipants(participantsData);
      setAdmins(adminsData);
      setPermissions(permissionsData);

      setChampion(null);

      if (tournamentData?.status === 'completed') {
        const championData = await getTournamentChampionUseCase.execute(id);
        setChampion(championData);
      }
    } catch (error) {
      logger.error('TournamentDetail', 'Failed to load data', { error });
      platformAlert(t('common.error'), t('tournaments.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLocation = async () => {
    if (!tournament || !id) return;

    try {
      await tournamentRepository.update(id, {
        location: locationName.trim() || undefined,
        locationLat: selectedLocation?.latitude,
        locationLng: selectedLocation?.longitude,
      });

      setTournament({
        ...tournament,
        location: locationName.trim() || undefined,
        locationLat: selectedLocation?.latitude,
        locationLng: selectedLocation?.longitude,
      });

      setEditingLocation(false);
      platformAlert(t('common.success'), t('alerts.locationUpdated'));
    } catch (error: any) {
      logger.error('TournamentDetail', 'Failed to update location', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleSaveDate = async () => {
    if (!tournament || !id) return;

    try {
      await tournamentRepository.update(id, {
        startDate: selectedDate,
      });

      setTournament({
        ...tournament,
        startDate: selectedDate,
      });

      setEditingDate(false);
      platformAlert(t('common.success'), t('alerts.dateUpdated'));
    } catch (error: any) {
      logger.error('TournamentDetail', 'Failed to update date', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleOpenMaps = () => {
    if (!tournament?.locationLat || !tournament?.locationLng) return;

    const latLng = `${tournament.locationLat},${tournament.locationLng}`;
    const label = encodeURIComponent(tournament.location || 'Tournament Location');

    const url = Platform.select({
      ios: `maps:?q=${label}&ll=${latLng}`,
      android: `geo:${latLng}?q=${label}`,
      web: `https://www.google.com/maps/search/?api=1&query=${latLng}`,
      default: `https://www.google.com/maps/search/?api=1&query=${latLng}`,
    });

    if (Platform.OS === 'web') {
      window.open(url, '_blank');
    } else {
      Linking.openURL(url!);
    }
  };

  const handleAddRegisteredParticipant = async () => {
    if (!selectedParticipantUser) {
      platformAlert(t('common.error'), t('tournaments.searchByNameOrEmail'));
      return;
    }

    if (!user || !tournament) return;

    try {
      setSearchingParticipant(true);
      await addRegisteredParticipantUseCase.execute({
        tournamentId: tournament.id,
        userId: selectedParticipantUser.id,
        userName: selectedParticipantUser.name || selectedParticipantUser.email.split('@')[0],
      });

      // Send tournament invitation notification
      try {
        await sendNotificationUseCase.execute(
          selectedParticipantUser.id,
          'tournament_invitation',
          t('notifications.tournamentInvitation'),
          t('notifications.tournamentInvitationBody', { name: tournament.name }),
          { tournamentId: tournament.id }
        );
      } catch {}

      platformAlert(t('common.success'), t('alerts.participantAdded', { email: selectedParticipantUser.email }));
      setParticipantSearchQuery('');
      setParticipantSearchResults([]);
      setSelectedParticipantUser(null);
      setAddParticipantModalVisible(false);
      await loadData();
    } catch (error: any) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else if (error instanceof Error && error.message === 'Tournament is full') {
        platformAlert(t('common.error'), error.message);
      } else {
        logger.error('TournamentDetail', 'Failed to add registered participant', { error });
        platformAlert(t('common.error'), error.message || t('errors.failedToLoad'));
      }
    } finally {
      setSearchingParticipant(false);
    }
  };

  const handleAddParticipant = async () => {
    if (!participantName.trim()) {
      platformAlert(t('common.error'), t('alerts.enterParticipantName'));
      return;
    }

    if (!id) return;

    if (tournament?.status !== 'setup') {
      platformAlert(t('common.error'), t('alerts.cannotAddStarted'));
      return;
    }

    try {
      setAdding(true);
      await addParticipantUseCase.execute({
        tournamentId: id,
        name: participantName.trim(),
        seed: participants.length + 1,
        userId: user?.id,
      });
      setParticipantName('');
      await loadData();
    } catch (error) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else if (error instanceof Error && error.message === 'Tournament is full') {
        platformAlert(t('common.error'), error.message);
      } else {
        logger.error('TournamentDetail', 'Failed to add participant', { error });
        platformAlert(t('common.error'), t('errors.failedToLoad'));
      }
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteParticipant = async (participantId: string) => {
    if (tournament?.status !== 'setup') {
      platformAlert(t('common.error'), t('alerts.cannotDeleteStarted'));
      return;
    }

    platformAlert(
      t('common.delete'),
      t('alerts.confirmDeleteParticipant', { name: t('tournaments.participant') }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteParticipantUseCase.execute(participantId);
              await loadData();
            } catch (error) {
              logger.error('TournamentDetail', 'Failed to delete participant', { error });
              platformAlert(t('common.error'), t('errors.failedToLoad'));
            }
          },
        },
      ]
    );
  };

  const handleStartTournament = async () => {
    if (!id) return;

    if (tournament?.tournamentType !== 'round_robin') {
      if (!isValidParticipantCount(participants.length)) {
        platformAlert(
          t('tournaments.invalidParticipantCount'),
          t('tournaments.invalidParticipantCountWarning', { count: participants.length })
        );
        return;
      }
    }

    if (participants.length < 2) {
      platformAlert(t('common.error'), t('alerts.needMinParticipants'));
      return;
    }

    const totalMatches = tournament?.tournamentType === 'round_robin'
      ? (participants.length * (participants.length - 1)) / 2
      : participants.length - 1;

    platformAlert(
      t('tournaments.startTournament'),
      t('alerts.confirmStartTournament', { count: participants.length }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('tournaments.startTournament'),
          onPress: async () => {
            try {
              setGenerating(true);
              await generateBracketUseCase.execute(id);
              showAd();

              // Notify all registered participants that the tournament started
              try {
                for (const p of participants) {
                  if (p.userId && p.userId !== user?.id) {
                    await sendNotificationUseCase.execute(
                      p.userId,
                      'tournament_started',
                      t('notifications.tournamentStarted'),
                      t('notifications.tournamentStartedBody', { name: tournament?.name || '' }),
                      { tournamentId: id }
                    );
                  }
                }
              } catch {}

              platformAlert(t('common.success'), t('alerts.bracketGenerated'), [
                {
                  text: t('tournaments.viewBracket'),
                  onPress: () => router.push(`/bracket/${id}`),
                },
              ]);
              await loadData();
            } catch (error: any) {
              logger.error('TournamentDetail', 'Failed to generate bracket', { error });
              platformAlert(t('common.error'), error.message || t('alerts.failedToGenerateBracket'));
            } finally {
              setGenerating(false);
            }
          },
        },
      ]
    );
  };

  const handleViewBracket = () => {
    if (id) {
      router.push(`/bracket/${id}`);
    }
  };

  const handleSaveRules = async () => {
    if (!tournament || !id) return;

    try {
      await tournamentRepository.update(id, { rules: rulesText.trim() || undefined });
      setTournament({ ...tournament, rules: rulesText.trim() || undefined });
      setEditingRules(false);
      platformAlert(t('common.success'), t('alerts.rulesUpdated'));
    } catch (error: any) {
      logger.error('TournamentDetail', 'Failed to update rules', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleAddAdmin = async () => {
    if (!searchEmail.trim()) {
      platformAlert(t('common.error'), t('alerts.enterEmail'));
      return;
    }

    if (!user || !tournament) return;

    try {
      setSearchingUser(true);
      const foundUser = await searchUserByEmailUseCase.execute(searchEmail.trim());

      if (!foundUser) {
        platformAlert(t('alerts.userNotFound'), t('alerts.userNotFound'));
        return;
      }

      await addTournamentAdminUseCase.execute({
        tournamentId: tournament.id,
        userId: foundUser.id,
        userEmail: foundUser.email,
        role: selectedRole,
        requestingUserId: user.id,
      });

      platformAlert(t('common.success'), t('alerts.adminAdded', { email: foundUser.email }));
      setSearchEmail('');
      setAddAdminModalVisible(false);
      setSelectedRole('admin');
      await loadData();
    } catch (error: any) {
      logger.error('TournamentDetail', 'Failed to add admin', { error });
      platformAlert(t('common.error'), error.message || t('errors.failedToLoad'));
    } finally {
      setSearchingUser(false);
    }
  };

  const handleRemoveAdmin = (admin: TournamentAdmin) => {
    if (!user || !tournament) return;

    platformAlert(
      t('common.remove'),
      `${t('common.remove')} ${admin.userEmail || t('profile.profile')}?`,
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.remove'),
          style: 'destructive',
          onPress: async () => {
            try {
              await removeTournamentAdminUseCase.execute({
                tournamentId: tournament.id,
                userId: admin.userId,
                requestingUserId: user.id,
              });
              platformAlert(t('common.success'), t('alerts.adminRemoved'));
              await loadData();
            } catch (error: any) {
              logger.error('TournamentDetail', 'Failed to remove admin', { error });
              platformAlert(t('common.error'), error.message || t('errors.failedToLoad'));
            }
          },
        },
      ]
    );
  };

  const handleCopyLink = async () => {
    const shareUrl = `https://tournaly.app/public/${id}`;
    await Clipboard.setStringAsync(shareUrl);
    platformAlert(t('common.copied'), t('alerts.linkCopied'));
  };

  const handleShareInvite = async () => {
    if (!tournament) return;

    const shareUrl = `https://tournaly.app/public/${id}`;
    const message = `Join "${tournament.name}" tournament on Tournaly!\n\n${shareUrl}`;

    try {
      await Share.share({
        message,
        title: `Join ${tournament.name}`,
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      logger.error('TournamentDetail', 'Failed to share', { error });
    }
  };

  const handleTogglePublic = async (value: boolean) => {
    if (!tournament || !id) return;

    // Going private: check private tournament limit
    if (!value) {
      const access = await checkAccess({ feature: 'private_tournament' });
      if (!access.allowed) {
        platformAlert(t('subscription.featureLocked'), t('subscription.privateTournamentLimitReached'), [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('subscription.upgradeToPro'), onPress: () => router.push('/paywall') },
        ]);
        return;
      }
    }

    try {
      await tournamentRepository.update(id, { isPublic: value });

      setTournament({ ...tournament, isPublic: value });

      if (value) {
        platformAlert(
          t('tournaments.publicTournament'),
          t('alerts.tournamentNowPublic'),
          [{ text: t('common.ok') }]
        );
      } else {
        platformAlert(t('tournaments.privateTournament'), t('alerts.tournamentNowPrivate'));
      }
    } catch (error: any) {
      logger.error('TournamentDetail', 'Failed to toggle public', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleGenerateInviteCode = async () => {
    if (!tournament || !id) return;

    try {
      const code = await tournamentInvitationDataSource.generateInviteCode(id);
      setInviteCode(code);
      setTournament({ ...tournament, inviteCode: code });
      platformAlert(t('common.success'), t('invitations.codeGenerated'));
    } catch (error) {
      logger.error('TournamentDetail', 'Failed to generate invite code', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleCopyInviteCode = async () => {
    if (!inviteCode) return;
    await Clipboard.setStringAsync(inviteCode);
    platformAlert(t('common.copied'), t('invitations.codeCopied'));
  };

  const handleShareInviteCode = async () => {
    if (!tournament || !inviteCode) return;

    const message = t('invitations.shareCodeMessage', { name: tournament.name, code: inviteCode });

    try {
      await Share.share({
        message,
        title: t('invitations.joinByCode'),
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      logger.error('TournamentDetail', 'Failed to share invite code', { error });
    }
  };

  const handleSendInvitation = async () => {
    if (!inviteEmail.trim()) {
      platformAlert(t('common.error'), t('alerts.enterEmail'));
      return;
    }

    if (!user || !tournament) return;

    try {
      setSendingInvite(true);
      const foundUser = await searchUserByEmailUseCase.execute(inviteEmail.trim());

      if (!foundUser) {
        platformAlert(t('alerts.userNotFound'), t('alerts.userNotFoundRegistered'));
        return;
      }

      await sendTournamentInvitationUseCase.execute({
        tournamentId: tournament.id,
        inviterId: user.id,
        inviteeId: foundUser.id,
        message: inviteMessage.trim() || undefined,
      });

      // Send push notification
      try {
        await sendNotificationUseCase.execute(
          foundUser.id,
          'tournament_invitation',
          t('notifications.tournamentInvitation'),
          t('notifications.tournamentInvitationBody', { name: tournament.name }),
          { tournamentId: tournament.id }
        );
      } catch {}

      platformAlert(t('common.success'), t('invitations.invitationSent'));
      setInviteEmail('');
      setInviteMessage('');
      setInviteModalVisible(false);
    } catch (error: any) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else {
        logger.error('TournamentDetail', 'Failed to send invitation', { error });
        platformAlert(t('common.error'), error.message || t('errors.failedToLoad'));
      }
    } finally {
      setSendingInvite(false);
    }
  };

  const handleParticipantPress = (participantId: string) => {
    router.push(`/profile/${participantId}`);
  };

  const handleEditTournament = () => {
    router.push(`/edit-tournament/${id}`);
  };

  if (loading) {
    return <TournamentDetailScreenLoading colors={colors} />;
  }

  if (!tournament) {
    return <TournamentDetailScreenError colors={colors} message={t('tournaments.tournamentNotFound')} />;
  }

  const isSetup = tournament.status === 'setup';
  const isValidCount = tournament.tournamentType === 'round_robin' ? participants.length >= 2 : isValidParticipantCount(participants.length);

  // Detect challenge tournaments (1v1 matches created from challenges)
  const isChallengeTournament = tournament.name.startsWith('Challenge:') && tournament.maxParticipants === 2;
  const isLeagueTournament = !!tournament.leagueId;

  return (
    <>
      <Stack.Screen options={{ title: tournament.name }} />
      <TournamentDetailScreenView
        tournament={tournament}
        participants={participants}
        champion={champion}
        admins={admins}
        participantName={participantName}
        setParticipantName={setParticipantName}
        adding={adding}
        isSetup={isSetup}
        canManageParticipants={canManageParticipants}
        isValidCount={isValidCount}
        generating={generating}
        isChallengeTournament={isChallengeTournament}
        isLeagueTournament={isLeagueTournament}
        onAddParticipant={handleAddParticipant}
        onAddRegisteredParticipant={handleAddRegisteredParticipant}
        onDeleteParticipant={handleDeleteParticipant}
        onParticipantPress={handleParticipantPress}
        onStartTournament={handleStartTournament}
        onViewBracket={handleViewBracket}
        canManageAdmins={canManageAdmins}
        onAddAdmin={handleAddAdmin}
        onRemoveAdmin={handleRemoveAdmin}
        rulesText={rulesText}
        setRulesText={setRulesText}
        editingRules={editingRules}
        setEditingRules={setEditingRules}
        onSaveRules={handleSaveRules}
        userRole={permissions.userRole}
        canEditTournament={canEditTournament}
        editingLocation={editingLocation}
        locationName={locationName}
        selectedLocation={selectedLocation}
        setLocationName={setLocationName}
        setSelectedLocation={setSelectedLocation}
        setEditingLocation={setEditingLocation}
        onSaveLocation={handleSaveLocation}
        editingDate={editingDate}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        setEditingDate={setEditingDate}
        showDatePicker={showDatePicker}
        onSaveDate={handleSaveDate}
        onTogglePublic={handleTogglePublic}
        onCopyLink={handleCopyLink}
        onShareInvite={handleShareInvite}
        onEditTournament={handleEditTournament}
        onOpenMaps={handleOpenMaps}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        addAdminModalVisible={addAdminModalVisible}
        setAddAdminModalVisible={setAddAdminModalVisible}
        searchEmail={searchEmail}
        setSearchEmail={setSearchEmail}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        searchingUser={searchingUser}
        addParticipantModalVisible={addParticipantModalVisible}
        setAddParticipantModalVisible={setAddParticipantModalVisible}
        participantSearchQuery={participantSearchQuery}
        setParticipantSearchQuery={setParticipantSearchQuery}
        participantSearchResults={participantSearchResults}
        selectedParticipantUser={selectedParticipantUser}
        onSelectParticipantUser={setSelectedParticipantUser}
        searchingParticipant={searchingParticipant}
        inviteModalVisible={inviteModalVisible}
        setInviteModalVisible={setInviteModalVisible}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteMessage={inviteMessage}
        setInviteMessage={setInviteMessage}
        sendingInvite={sendingInvite}
        inviteCode={inviteCode}
        onGenerateInviteCode={handleGenerateInviteCode}
        onCopyInviteCode={handleCopyInviteCode}
        onShareInviteCode={handleShareInviteCode}
        onSendInvitation={handleSendInvitation}
        colors={colors}
      />
    </>
  );
}
